import {
  Box,
  Button,
  Flex,
  Icon,
  Input,
  InputElement,
  Spinner,
} from '@chakra-ui/react';

import { CustomSelectStateProps } from '@monetize/types/app';
import React, { useMemo } from 'react';
import { MdArrowDropDown, MdClose } from 'react-icons/md';
import MCustomIconButton from '../../MCustomIconButton';
import { useCustomSelectInput } from './useCustomSelectInput';

export interface MainInputProps extends Pick<
  CustomSelectStateProps,
  | 'getTitleFromValue'
  | 'disabled'
  | 'isReadOnly'
  | 'onOpen'
  | 'onClose'
  | 'loading'
  | 'loadingRenderer'
  | 'value'
  | 'onToggleOpen'
  | 'externalLoading'
  | 'placeholder'
  | 'isOpen'
  | 'items'
  | 'cursor'
  | 'setCursor'
  | 'handleChange'
  | 'showContentInInput'
  | 'renderContentInInput'
  | 'inputProps'
  | 'renderRightElement'
  | 'clearable'
  | 'onClear'
  | 'onChange'
  | 'queryResultCount'
  | 'query'
  | 'onQueryKeyDown'
  | 'onQueryChange'
  | 'displayAvatar'
  | 'renderInputLeftElement'
  | 'inputLeftElementContent'
  | 'isSubtitleItem'
  | 'isTruncatedInputTitle'
  | 'showQueryInput'
  | 'useMainInputAsSearchInput'
  | 'containerId'
> {
  ref: React.LegacyRef<HTMLInputElement>;
}

export const MainInput = React.forwardRef<HTMLInputElement, MainInputProps>(
  (props, ref) => {
    const {
      clearable,
      disabled,
      externalLoading,
      inputLeftElementContent,
      inputProps,
      isOpen,
      isReadOnly,
      loading,
      loadingRenderer,
      onOpen,
      placeholder,
      renderContentInInput,
      showContentInInput,
      value,
      getTitleFromValue,
    } = props;

    const {
      bgColor,
      borderColor,
      handleFocusInput,
      handleOnClear,
      handleToggleOpen,
      inputValue,
      rightElement,
      rightMargin,
      styles,
    } = useCustomSelectInput(props, ref);

    const hasValue = Array.isArray(value) ? value.length >= 1 : !!value;

    const tooltipMessage = useMemo(() => {
      if (Array.isArray(value) && value.length > 1) {
        return value.map(getTitleFromValue).join(', ');
      }
      return '';
    }, [value]);

    // Both wrappers stand in for v2's `InputGroup`, whose `isolation: isolate`
    // kept `InputElement`'s `zIndex: 2` local to the input. Without it the
    // dropdown icon paints over sticky table cells (pinned columns use
    // `zIndex: 1`) when the row scrolls under them.
    if (!showContentInInput) {
      return (
        <Box
          position="relative"
          isolation="isolate"
          w="100%"
          onClick={handleToggleOpen}
        >
          {inputLeftElementContent && (
            <InputElement placement="start" w="8" px="0" top="0">
              {inputLeftElementContent}
            </InputElement>
          )}
          <Input
            type="text"
            ref={ref}
            placeholder={placeholder}
            value={inputValue}
            disabled={disabled}
            readOnly={isReadOnly}
            variant={isReadOnly ? 'readonly' : 'outline'}
            bg={bgColor}
            color={isOpen ? 'tIndigo.base' : undefined}
            tabIndex={isOpen ? -1 : 0}
            // v2's `InputGroup` reserved room for the left element itself; v3
            // doesn't, so without this the value/placeholder sits under it.
            pl={inputLeftElementContent ? 8 : undefined}
            pr={rightMargin}
            cursor={disabled ? 'not-allowed' : 'pointer'}
            userSelect="none"
            css={{
              caretColor: 'transparent',
            }}
            _placeholder={{
              color: 'tGray.lightPurple',
            }}
            title={tooltipMessage || inputValue}
            onChange={() => {}}
            {...inputProps}
          />

          {!isReadOnly && (
            <InputElement placement="end" width={rightMargin} px="0" top="0">
              {rightElement?.content}
              {clearable && hasValue && !disabled && !loading && (
                <MCustomIconButton
                  icon={MdClose}
                  aria-label="Clear"
                  bg="none"
                  color="tPurple.dark"
                  _hover={{
                    bg: 'none',
                  }}
                  ml="2"
                  btnSize={4}
                  padding={0}
                  containerSize={4}
                  onClick={handleOnClear}
                />
              )}
              {loading || externalLoading ? (
                <>{loadingRenderer ? loadingRenderer : <Spinner size="xs" />}</>
              ) : (
                <Button
                  variant="icon"
                  padding="0"
                  minWidth="initial"
                  _hover={{ background: 'none' }}
                  _focus={{ background: 'none', boxShadow: 'none' }}
                  tabIndex={-1}
                >
                  <Icon
                    size="md"
                    zIndex="1"
                    color={disabled ? 'tGray.acGray' : 'tPurple.dark'}
                    opacity={disabled ? 0.25 : 'unset'}
                    asChild
                  >
                    <MdArrowDropDown />
                  </Icon>
                </Button>
              )}
            </InputElement>
          )}
        </Box>
      );
    }

    return (
      <Box
        css={styles}
        bg={bgColor}
        borderColor={borderColor}
        display="flex"
        flexWrap="wrap"
        position="relative"
        isolation="isolate"
        w="100%"
        alignItems="center"
        cursor="pointer"
        onMouseDown={handleToggleOpen}
      >
        <Box display="flex" flexWrap="wrap" w="full" pr="8" alignItems="center">
          {showContentInInput &&
            renderContentInInput &&
            renderContentInInput({ onToggleOpen: handleToggleOpen })}
          {!renderContentInInput && (
            <Flex alignItems="center" flex="1" display="grid">
              <Input
                type="text"
                ref={ref}
                placeholder={placeholder}
                onChange={() => {}}
                value={!showContentInInput ? inputValue : ''}
                onFocus={handleFocusInput}
                disabled={disabled}
                readOnly={isReadOnly}
                tabIndex={isOpen ? -1 : 0}
                border="none"
                _focus={{ border: 'none' }}
                width="auto"
                bg="none"
                _placeholder={{
                  color: 'tGray.darkGrayPurple',
                }}
                title={tooltipMessage || inputValue}
                {...inputProps}
              />
            </Flex>
          )}
        </Box>
        <Box position="absolute" right="0" display="flex" alignItems="center">
          {loading || externalLoading ? (
            <>{loadingRenderer ? loadingRenderer : <Spinner size="xs" />}</>
          ) : (
            <Button
              variant="icon"
              padding="0"
              minWidth="initial"
              _hover={{ background: 'none' }}
              _focus={{ background: 'none', boxShadow: 'none' }}
              tabIndex={-1}
            >
              <Icon fontSize="20" zIndex="1" asChild>
                <MdArrowDropDown />
              </Icon>
            </Button>
          )}
        </Box>
      </Box>
    );
  },
);
